import { useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { object as yupObject } from 'yup'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useYupFields } from 'hooks/forms/useYupFields'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useAllowedCountriesQuery } from 'hooks/queries/useAllowedCountriesQuery'
import type { CreateCustomerAgreement } from 'types/customers'
import { getCountryOptions } from 'utils/forms'
import { BUSINESS_USE_CASES } from '../helpers/constants'
import { findLabel, getOptions } from '../utils/utils'

const DEFAULT_VALUES: CreateCustomerAgreement = {
  reference: '',
  useCase: '',
  customerType: 'COMPANY',
  countryCode: '',
  customerName: '',
}

export const useAddCustomerAgreementForm = (defaultValues?: CreateCustomerAgreement) => {
  const { t } = useTranslation()
  const { getRequiredYupField } = useYupFields()

  const { data: countries = [], isFetching: isFetchingCountries } = useAllowedCountriesQuery()
  const countryOptions = useMemo(() => getCountryOptions(countries), [countries])
  const useCaseOptions = useMemo(
    () => getOptions(BUSINESS_USE_CASES, 'customers.useCaseOptions', t),
    [t]
  )

  const formSchema = yupObject({
    reference: getRequiredYupField({ label: t('customers.externalReference') }),
    customerName: getRequiredYupField({ label: t('customers.businessName') }),
    useCase: getRequiredYupField({ label: t('customers.useCase') }),
    countryCode: getRequiredYupField({ label: t('customers.countryCode') }),
  })

  const form = useForm<CreateCustomerAgreement>({
    defaultValues: defaultValues ?? DEFAULT_VALUES,
    mode: 'onChange',
    resolver: yupFormResolver<CreateCustomerAgreement>(formSchema),
  })

  const { handleSubmit, control } = form
  const formValues = useWatch({ control })
  const hasErrors = !!Object.keys(form.formState.errors).length

  const { setInputField, setComboboxField } = useFormFields<keyof CreateCustomerAgreement>(
    formSchema,
    form,
    formValues
  )

  const buildDisplayData = (data: CreateCustomerAgreement) => ({
    useCase: findLabel(useCaseOptions, data.useCase),
    countryCode: findLabel(countryOptions, data.countryCode),
  })

  return {
    formValues,
    formSchema,
    hasErrors,
    handleSubmit,
    setInputField,
    setComboboxField,
    useCaseOptions,
    countryOptions,
    isFetchingCountries,
    buildDisplayData,
  }
}
